// 노션식 드래그핸들(⋮⋮) 메뉴 — rhoot 에디터 전용.
//
// 배경: BlockNote(@blocknote/react·mantine 0.51.4)의 기본 드래그핸들 메뉴는
//       "삭제 / 색상" 정도만 들어 있어 거의 비어 보였다. 노션처럼 블록 조작
//       (복제·블록 유형 변경·위/아래 이동·위/아래 블록 추가)을 채워 넣는다.
//
// 동작 방식:
//   - PageEditor 에서 <BlockNoteView sideMenu={false}> 로 기본 사이드 메뉴를 끄고,
//     <SideMenuController sideMenu={renderSideMenu} /> 로 우리 메뉴를 렌더.
//   - 드래그핸들 메뉴 안의 "현재 블록"은 BlockNote 가 내부적으로 쓰는 패턴 그대로
//     useExtensionState(SideMenuExtension, { editor, selector: s => s?.block }) 로 구독.
//     (RemoveBlockItem/BlockColorsItem 빌트인이 쓰는 것과 동일 — 0.51.4 패키지에서 검증)
//   - 빌트인으로 충분한 항목(삭제·색상)은 RemoveBlockItem/BlockColorsItem 을 그대로 사용,
//     없는 기능(복제·Turn into·이동·위아래 추가)은 useComponentsContext().Generic.Menu.Item
//     + editor API 로 직접 구현.
//
// ⚠️ 스타일은 block-side-menu.css 에만. page-editor.css/public-page.css 는 건드리지 않음.

import {
  SideMenu,
  SideMenuController,
  AddBlockButton,
  DragHandleButton,
  DragHandleMenu,
  RemoveBlockItem,
  BlockColorsItem,
  useComponentsContext,
  useBlockNoteEditor,
  useExtensionState,
} from '@blocknote/react';
import { SideMenuExtension } from '@blocknote/core';
import './block-side-menu.css';

// "Turn into" 대상 — schema.js 에 등록된 기본 블록 + callout 기준.
// type/props 는 editor.updateBlock 에 그대로 전달된다(BlockNote 기본 타입명).
const TURN_INTO_ITEMS = [
  { type: 'paragraph', label: '텍스트', icon: '¶' },
  { type: 'heading', props: { level: 1 }, label: '제목 1', icon: 'H₁' },
  { type: 'heading', props: { level: 2 }, label: '제목 2', icon: 'H₂' },
  { type: 'heading', props: { level: 3 }, label: '제목 3', icon: 'H₃' },
  { type: 'bulletListItem', label: '불릿 리스트', icon: '•' },
  { type: 'numberedListItem', label: '번호 리스트', icon: '1.' },
  { type: 'checkListItem', label: '체크박스', icon: '☑' },
  { type: 'quote', label: '인용', icon: '❝' },
  { type: 'codeBlock', label: '코드', icon: '</>' },
];

// 현재 드래그핸들이 가리키는 블록을 구독. 메뉴 항목 컴포넌트들의 공통 훅.
function useHoveredBlock() {
  const editor = useBlockNoteEditor();
  const block = useExtensionState(SideMenuExtension, {
    editor,
    selector: (s) => s?.block,
  });
  return { editor, block };
}

// ── 복제 ─────────────────────────────────────────────────────────────────
// 현재 블록을 그대로 복사해 바로 아래에 삽입. content/props/children 유지.
function DuplicateItem() {
  const Components = useComponentsContext();
  const { editor, block } = useHoveredBlock();
  if (!block) return null;
  return (
    <Components.Generic.Menu.Item
      className="bn-menu-item"
      icon={<span className="rt-dh-ico" aria-hidden="true">⧉</span>}
      onClick={() => {
        const fresh = editor.getBlock(block.id) || block;
        // id 는 제외(새로 발급) — type/props/content/children 만 복제.
        const clone = {
          type: fresh.type,
          props: fresh.props,
          content: fresh.content,
          children: fresh.children,
        };
        editor.insertBlocks([clone], block.id, 'after');
      }}
    >
      복제
    </Components.Generic.Menu.Item>
  );
}

// ── 위에 블록 추가 / 아래에 블록 추가 ─────────────────────────────────────
function InsertBlockItem({ placement, label, icon }) {
  const Components = useComponentsContext();
  const { editor, block } = useHoveredBlock();
  if (!block) return null;
  return (
    <Components.Generic.Menu.Item
      className="bn-menu-item"
      icon={<span className="rt-dh-ico" aria-hidden="true">{icon}</span>}
      onClick={() => {
        const inserted = editor.insertBlocks(
          [{ type: 'paragraph' }],
          block.id,
          placement,
        );
        const newBlock = inserted && inserted[0];
        if (newBlock) {
          // 새 빈 단락에 커서 두어 바로 입력 가능하게.
          editor.setTextCursorPosition(newBlock.id, 'end');
          editor.focus();
        }
      }}
    >
      {label}
    </Components.Generic.Menu.Item>
  );
}

// ── 위로 이동 / 아래로 이동 ───────────────────────────────────────────────
function MoveBlockItem({ direction, label, icon }) {
  const Components = useComponentsContext();
  const { editor, block } = useHoveredBlock();
  if (!block) return null;
  return (
    <Components.Generic.Menu.Item
      className="bn-menu-item"
      icon={<span className="rt-dh-ico" aria-hidden="true">{icon}</span>}
      onClick={() => {
        if (direction === 'up') editor.moveBlocksUp(block.id);
        else editor.moveBlocksDown(block.id);
      }}
    >
      {label}
    </Components.Generic.Menu.Item>
  );
}

// ── 블록 유형 변경(Turn into) — 우측 서브메뉴 ─────────────────────────────
// BlockColorsItem 빌트인과 동일한 sub-menu 패턴(Root sub > Trigger sub > Dropdown sub).
function TurnIntoItem() {
  const Components = useComponentsContext();
  const { editor, block } = useHoveredBlock();
  if (!block) return null;
  return (
    <Components.Generic.Menu.Root position="right" sub>
      <Components.Generic.Menu.Trigger sub>
        <Components.Generic.Menu.Item
          className="bn-menu-item"
          subTrigger
          icon={<span className="rt-dh-ico" aria-hidden="true">⇄</span>}
        >
          블록 유형 변경
        </Components.Generic.Menu.Item>
      </Components.Generic.Menu.Trigger>
      <Components.Generic.Menu.Dropdown sub className="bn-menu-dropdown">
        {TURN_INTO_ITEMS.map((item) => {
          const isCurrent = block.type === item.type
            && (item.props?.level === undefined || block.props?.level === item.props.level);
          return (
            <Components.Generic.Menu.Item
              key={item.label}
              className="bn-menu-item"
              checked={isCurrent}
              icon={<span className="rt-dh-ico" aria-hidden="true">{item.icon}</span>}
              onClick={() => {
                editor.updateBlock(block.id, {
                  type: item.type,
                  ...(item.props ? { props: item.props } : {}),
                });
              }}
            >
              {item.label}
            </Components.Generic.Menu.Item>
          );
        })}
      </Components.Generic.Menu.Dropdown>
    </Components.Generic.Menu.Root>
  );
}

// 위험 액션(삭제) 라벨에 빨간 톤을 입히기 위한 래퍼. RemoveBlockItem 의 onClick 동작은
// 빌트인 그대로 두고, 라벨/아이콘만 우리 클래스로 감싼다.
function DeleteItemLabel() {
  return (
    <span className="rt-dh-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span className="rt-dh-ico" aria-hidden="true">🗑</span>
      삭제
    </span>
  );
}

// 드래그핸들(⋮⋮) 클릭 시 뜨는 메뉴 본체.
function RtDragHandleMenu() {
  const Components = useComponentsContext();
  return (
    <DragHandleMenu>
      {/* Turn into (서브메뉴) */}
      <TurnIntoItem />
      {/* 색상(텍스트/배경) — 빌트인. 지원 블록에서만 자동 렌더 */}
      <BlockColorsItem>색상</BlockColorsItem>

      <Components.Generic.Menu.Divider />

      {/* 복제 */}
      <DuplicateItem />
      {/* 위/아래 블록 추가 */}
      <InsertBlockItem placement="before" label="위에 블록 추가" icon="↥" />
      <InsertBlockItem placement="after" label="아래에 블록 추가" icon="↧" />

      <Components.Generic.Menu.Divider />

      {/* 위/아래 이동 */}
      <MoveBlockItem direction="up" label="위로 이동" icon="↑" />
      <MoveBlockItem direction="down" label="아래로 이동" icon="↓" />

      <Components.Generic.Menu.Divider />

      {/* 삭제 — 빌트인 동작 + 위험 톤 라벨 */}
      <RemoveBlockItem>
        <DeleteItemLabel />
      </RemoveBlockItem>
    </DragHandleMenu>
  );
}

// SideMenuController 에 넘길 렌더 함수. 기본 사이드 메뉴 구성을 그대로 살리되
// (블록 추가 버튼 + 드래그핸들), 드래그핸들 메뉴만 우리 것으로 교체.
function renderSideMenu(props) {
  return (
    <SideMenu {...props}>
      <AddBlockButton {...props} />
      <DragHandleButton {...props} dragHandleMenu={RtDragHandleMenu} />
    </SideMenu>
  );
}

// PageEditor 에서 <BlockNoteView sideMenu={false}> 아래 자식으로 렌더.
export default function BlockSideMenu() {
  return <SideMenuController sideMenu={renderSideMenu} />;
}
